/**
 * app/screens/ViewItemScreen.tsx
 * -------------------------------
 * What: Read-only detail view for a single item including dynamic attributes and images.
 * Why: Presents a clean summary with actions to edit or attach receipts.
 */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  SafeAreaView,
  StatusBar,
  ScrollView,
  ActivityIndicator,
  Alert,
  Image,
  useWindowDimensions,
  Linking,
  Platform,
  TouchableOpacity,
  BackHandler,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { useAuth } from '../../lib/AuthContext';
import { supabase } from '../../lib/supabase';
import { Ionicons } from '@expo/vector-icons';
import Modal from 'react-native-modal';
import { useSubscription } from '../../lib/SubscriptionContext';
import { useTheme } from '../../lib/ThemeContext';
import { CommonActions } from '@react-navigation/native';
import { Modal as RNModal } from 'react-native';
import { useImageCache } from '../../lib/ImageCacheContext';
import ThemedButton from '../components/ThemedButton';
import type { AttributeDefinition } from '../components/DynamicAttributes';

// Type definitions restored
type Item = {
  id: string;
  name: string;
  make: string | null;
  model: string | null;
  description: string | null;
  room_id: string;
  serial_number: string | null;
  purchase_date: string | null;
  price: number | null;
  warranty_expiry: string | null;
  place_of_purchase: string | null;
  receipt_id: string | null;
  warranty_doc_url: string | null;
  rooms: {
    name: string;
  };
  subcategory?: {
    name: string;
    category: { name: string };
  };
  item_images: {
    id: string;
    url: string;
    order?: number;
  }[];
};

type ViewItemScreenNavigationProp = StackNavigationProp<RootStackParamList, 'ViewItem'>;
type ViewItemScreenRouteProp = RouteProp<RootStackParamList, 'ViewItem'>;

const ViewItemScreen = () => {
  const { width: screenWidth } = useWindowDimensions();
  const navigation = useNavigation<ViewItemScreenNavigationProp>();
  const route = useRoute<ViewItemScreenRouteProp>();
  const { itemId, returnPage } = route.params;
  const { user } = useAuth();
  const { currency, dateFormat, tier } = useSubscription();
  const { theme, themeType } = useTheme();
  // Ensure close icon is visible on all themes (white on light theme)
  const closeIconColor = themeType === 'light' ? '#ffffff' : theme.text;

  const glowStyle = themeType === 'blueNeon'
    ? (Platform.OS === 'web'
        ? { boxShadow: `0 0 6px 1px ${theme.accent}` } as any
        : {
            shadowColor: theme.accent,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.5,
            shadowRadius: 6,
            elevation: 6,
          })
    : {};
  const [item, setItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [signedImageUrls, setSignedImageUrls] = useState<string[]>([]);
  const [documents, setDocuments] = useState<{ id: string; type: string; name?: string | null; signedUrl: string }[]>([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [signedUrls, setSignedUrls] = useState<{ [key: string]: string }>({});
  const { getCachedUrl, setCachedUrl } = useImageCache();
  const getCachedUrlRef = React.useRef(getCachedUrl);
  const setCachedUrlRef = React.useRef(setCachedUrl);
  React.useEffect(() => {
    getCachedUrlRef.current = getCachedUrl;
    setCachedUrlRef.current = setCachedUrl;
  }, [getCachedUrl, setCachedUrl]);
  const [attributeDefs, setAttributeDefs] = useState<AttributeDefinition[]>([]);

  // Initial focus tracking to avoid duplicate fetch calls when the screen mounts
  const isInitialFocus = React.useRef(true);

  const isSmallScreen = screenWidth < 768; // Match ItemList breakpoint for mobile web

  const itemColumnStyle: any = {
    marginBottom: 12,
    width: isSmallScreen ? '100%' : '50%',
  };

  // Responsive row/label/value styles for desktop two-column layout vs mobile stacked layout
  const rowStyle: any = {
    flexDirection: isSmallScreen ? 'column' : 'row',
    alignItems: isSmallScreen ? 'flex-start' : 'center',
    marginBottom: 12,
  };

  const labelStyle: any = {
    color: theme.secondaryText,
    fontWeight: '500',
    fontSize: 14,
    marginBottom: isSmallScreen ? 2 : 0,
    textAlign: isSmallScreen ? 'left' : 'right',
    paddingRight: isSmallScreen ? 0 : 12,
    flexBasis: isSmallScreen ? 'auto' : 150,
  };

  const valueStyle: any = {
    color: theme.text,
    fontSize: 16,
    flex: 1,
    minWidth: 0,
  };

  // Fetch item and documents once on mount (and if itemId changes)
  useEffect(() => {
    if (itemId) {
      fetchItem();
      fetchDocuments();
    }
  }, [itemId]);

  useEffect(() => {
    if (!item || !item.item_images || item.item_images.length === 0) {
      setSignedImageUrls([]);
      return;
    }

    (async () => {
      const urlArray: string[] = new Array(item.item_images.length).fill('');
      const pathsToSign: string[] = [];
      const meta: Record<string, { index: number; cacheKey: string }> = {};

      item.item_images.forEach((img: {id:string;url:string;order?:number}, idx: number) => {
        if (!img.url) return;
        const cacheKey = `item-${item.id}-img-${img.id}-${img.order ?? 0}`;
        const cached = getCachedUrlRef.current(cacheKey);
        if (cached) {
          urlArray[idx] = cached;
        } else {
          pathsToSign.push(img.url);
          meta[img.url] = { index: idx, cacheKey };
        }
      });

      if (pathsToSign.length > 0) {
        const { data, error } = await supabase.storage
          .from('item-images')
          .createSignedUrls(pathsToSign, 60 * 60);

        if (error) {
          console.error('Error batch-signing item images:', error);
        }

        data?.forEach((signed) => {
          if (!signed.signedUrl || !signed.path) return;
          const { index, cacheKey } = meta[signed.path];
          urlArray[index] = signed.signedUrl;
          setCachedUrlRef.current(cacheKey, signed.signedUrl);
        });
      }

      setSignedImageUrls(urlArray);
    })();
  }, [item]);

  useEffect(() => {
    if (Platform.OS === 'android') {
      const backHandler = BackHandler.addEventListener('hardwareBackPress', handleBackPress);
      return () => backHandler.remove();
    }
  }, []);

  useFocusEffect(
    React.useCallback(() => {
      if (isInitialFocus.current) {
        // First time the screen is focused after mount; we already fetched via mount effect below
        isInitialFocus.current = false;
        return;
      }

      if (itemId) {
        fetchItem();
        fetchDocuments();
      }
    }, [itemId])
  );

  const fetchItem = async () => {
    if (!user || !itemId) return;

    try {
      const { data, error } = await supabase
        .from('items')
        .select(`
          *,
          rooms!items_room_id_fkey (
            name
          ),
          subcategory:item_subcategories!items_subcategory_id_fkey (
            name,
            category:item_categories (
              name
            )
          ),
          item_images (
            id,
            url,
            "order"
          )
        `)
        .eq('id', itemId)
        .single();

      if (error) throw error;
      if (data) {
        // Sort images by order ASC
        data.item_images = (data.item_images || []).sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
      setItem(data);
      // Load attribute definitions if subcategory_id is present
      try {
        const subId = (data as any).subcategory_id as string | null;
        if (subId) {
          const { data: defs } = await supabase
            .from('attribute_definitions')
            .select('id,key,label,data_type,required,options,ui_control,help_text,display_order')
            .eq('subcategory_id', subId)
            .order('display_order');
          setAttributeDefs((defs || []) as AttributeDefinition[]);
        } else {
          setAttributeDefs([]);
        }
      } catch {
        setAttributeDefs([]);
      }
      }
    } catch (error) {
      console.error('Error fetching item:', error);
      Alert.alert('Error', 'Failed to load item details');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    if (!user || !itemId) return;
    try {
      const docArr: { id: string; type: 'receipt' | 'warranty' | 'other'; name?: string | null; file_path: string }[] = [];

      // Fetch warranty docs from item_documents
      const { data: warrantyDocs, error: wErr } = await supabase
        .from('item_documents')
        .select('id, file_path')
        .eq('item_id', itemId)
        .eq('type', 'warranty');
      if (wErr) throw wErr;
      warrantyDocs?.forEach((d: any) => docArr.push({ id: d.id, type: 'warranty', file_path: d.file_path }));

      // Fetch additional (non-warranty) docs from item_documents
      const { data: otherDocs, error: oErr } = await supabase
        .from('item_documents')
        .select('id, file_path, name, type')
        .eq('item_id', itemId)
        .neq('type', 'warranty');
      if (oErr) throw oErr;
      otherDocs?.forEach((d: any) => docArr.push({ id: d.id, type: 'other', name: d.name, file_path: d.file_path }));

      // Fetch receipt via items.receipt_id
      const { data: itemRow, error: iErr } = await supabase
        .from('items')
        .select('receipt_id')
        .eq('id', itemId)
        .single();
      if (iErr) throw iErr;
      if (itemRow && itemRow.receipt_id) {
        const { data: receiptRow, error: rErr } = await supabase
          .from('receipts')
          .select('id, file_path')
          .eq('id', itemRow.receipt_id)
          .single();
        if (!rErr && receiptRow) {
          docArr.push({ id: receiptRow.id, type: 'receipt', file_path: receiptRow.file_path });
        }
      }

      if (docArr.length === 0) {
        setDocuments([]);
        return;
      }

      // Generate signed URLs
      const docsWithUrls = await Promise.all(
        docArr.map(async (doc) => {
          if (!doc.file_path) return null;
          const path = doc.file_path.startsWith(`${user.id}/`) ? doc.file_path : `${user.id}/${doc.file_path}`;
          const suggestedName =
            doc.type === 'receipt' ? 'Receipt.pdf' :
            doc.type === 'warranty' ? 'Warranty.pdf' :
            (doc.name && doc.name.trim().length > 0 ? doc.name : 'Document');
          const { data: urlData, error: urlError } = await supabase.storage
            .from('item-documents')
            .createSignedUrl(path, 3600, { download: suggestedName });
          if (urlError || !urlData) return null;
          return { id: doc.id, type: doc.type, name: doc.name, signedUrl: urlData.signedUrl };
        })
      );
      setDocuments(docsWithUrls.filter(Boolean) as { id: string; type: string; name?: string | null; signedUrl: string }[]);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    
    switch (dateFormat) {
      case 'DD/MM/YYYY':
        return date.toLocaleDateString('en-GB');
      case 'MM/DD/YYYY':
        return date.toLocaleDateString('en-US');
      case 'YYYY-MM-DD':
        return date.toISOString().split('T')[0];
      default:
        return date.toLocaleDateString('en-GB');
    }
  };

  const formatPrice = (price: number | null) => {
    if (price === null) return null;
    
    const currencySymbols: { [key: string]: string } = {
      'GBP': '£',
      'USD': '$',
      'EUR': '€'
    };
    
    return `${currencySymbols[currency]}${price.toFixed(2)}`;
  };

  const handleEdit = () => {
    navigation.navigate('EditItem', { itemId, returnPage });
  };

  const handleOpenDocument = async (url: string, type: 'receipt' | 'warranty') => {
    try {
      const supported = await Linking.canOpenURL(url);
      
      if (supported) {
        await Linking.openURL(url);
      } else {
        Alert.alert(
          'Error',
          `Cannot open ${type}. The URL may be invalid or the device doesn't support opening this type of file.`
        );
      }
    } catch (error) {
      console.error(`Error opening ${type}:`, error);
      Alert.alert(
        'Error',
        `Failed to open ${type}. Please try again later.`
      );
    }
  };

  const handleImagePress = (index: number) => {
    setSelectedImageIndex(index);
    setImageViewerVisible(true);
  };

  const handleNextImage = () => {
    if (item?.item_images && selectedImageIndex < item.item_images.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  const handlePrevImage = () => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const handleBackPress = () => {
    navigation.goBack();
    if (returnPage) {
      navigation.dispatch(
        CommonActions.navigate({
          name: 'ItemsTab',
          params: { page: returnPage }
        })
      );
    }
    return true;
  };

  if (loading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.background }]}>
        <ActivityIndicator size="large" color={theme.accent} />
      </View>
    );
  }

  if (!item) {
    return (
      <View style={[styles.errorContainer, { backgroundColor: theme.background }]}>
        <Text style={[styles.errorText, { color: theme.text }]}>Item not found</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <StatusBar barStyle="light-content" />
      {/* Fixed Header */}
      <View style={{ paddingTop: 32, paddingBottom: 4, paddingHorizontal: 20, alignItems: 'center', backgroundColor: theme.background, flexDirection:'row', justifyContent:'center' }}>
        {/* back */}
        <Pressable
          style={({ pressed }) => ({ position:'absolute', left:20, top:32, padding: 8, borderRadius: 20, backgroundColor: theme.inputBackground, opacity: pressed?0.7:1, zIndex:2 })}
          onPress={() => navigation.goBack()}
          accessibilityLabel="Back"
          accessibilityRole="button"
        >
          <Ionicons name="arrow-back" size={26} color={theme.text} />
        </Pressable>

        {/* edit */}
        <Pressable
          style={({ pressed }) => ({ position:'absolute', right:20, top:32, padding: 8, borderRadius: 20, backgroundColor: theme.accent, opacity: pressed?0.7:1, zIndex:2 })}
          onPress={handleEdit}
          accessibilityLabel="Edit item"
          accessibilityRole="button"
        >
          <Ionicons name="pencil" size={22} color={theme.buttonText} />
        </Pressable>

        <View style={{ alignItems:'center', paddingHorizontal: 64, flex: 1 }}>
          <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', textAlign: 'center', marginBottom: 4 }} numberOfLines={2}>
            {item.name.replace(/\w\S*/g, (txt: string) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())}
          </Text>
          <Text style={{ color: theme.secondaryText, fontSize: 14, marginTop: 2, textAlign: 'center' }} numberOfLines={2}>
            {item.rooms?.name ? item.rooms.name.replace(/\w\S*/g, (txt: string) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()) : ''}
          </Text>
          <View style={{ height: 2, backgroundColor: theme.border, width: 60, borderRadius: 2, marginTop: 8 }} />
        </View>
      </View>

      <ScrollView contentContainerStyle={[styles.scrollContent, { paddingTop: 4, ...(isSmallScreen ? { paddingHorizontal: 16 } : {}) }]}>
        {/* IMAGE/LIGHTBOX SECTION */}
        <View style={{ alignItems: 'center', marginTop: 8, marginBottom: 16 }}>
          {item.item_images && item.item_images.length > 0 ? (
              <>
              <Pressable
                onPress={() => handleImagePress(selectedImageIndex)}
                  style={({ pressed }) => [{
                  width: '100%',
                    maxWidth: isSmallScreen ? undefined : 600,
                    height: isSmallScreen ? 200 : 300,
                  borderRadius: 16,
                    backgroundColor: theme.inputBackground,
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                    opacity: pressed ? 0.9 : 1,
                  }, glowStyle]}
              >
                <Image
                  source={{ uri: signedImageUrls[selectedImageIndex] || '' }}
                  style={styles.mainImage}
                  resizeMode="contain"
                />
              </Pressable>
              {item.item_images.length > 1 && (
                  <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 8, paddingVertical: 10 }}>
                    <Pressable
                      onPress={() => setSelectedImageIndex(Math.max(selectedImageIndex - 1, 0))}
                      disabled={selectedImageIndex === 0}
                      style={({ pressed }) => [{ padding: 6, opacity: selectedImageIndex === 0 ? 0.3 : pressed ? 0.7 : 1 }]}
                    >
                      <Ionicons name="chevron-back" size={22} color={theme.text} />
                    </Pressable>
                  {item.item_images.map((pic, idx) => (
                      <Pressable
                        key={pic.id}
                        onPress={() => setSelectedImageIndex(idx)}
                        style={{ width: 8, height: 8, borderRadius: 4, marginHorizontal: 2, backgroundColor: idx === selectedImageIndex ? theme.accent : theme.secondaryText, opacity: idx === selectedImageIndex ? 1 : 0.4 }}
                      />
                    ))}
                    <Pressable
                    onPress={() => setSelectedImageIndex(Math.min(selectedImageIndex + 1, item.item_images.length - 1))}
                    disabled={selectedImageIndex === item.item_images.length - 1}
                    style={({ pressed }) => [{ padding: 6, opacity: selectedImageIndex === item.item_images.length - 1 ? 0.3 : pressed ? 0.7 : 1 }]}
                    >
                      <Ionicons name="chevron-forward" size={22} color={theme.text} />
                      </Pressable>
                </View>
              )}
              </>
          ) : (
            <View style={{ width: 400, height: 220, justifyContent: 'center', alignItems: 'center', borderRadius: 16, backgroundColor: theme.inputBackground, ...glowStyle }}>
                <Ionicons name="image-outline" size={64} color={theme.secondaryText} />
                <Text style={{ color: theme.secondaryText, fontSize: 16, marginTop: 8 }}>No Image</Text>
            </View>
          )}
            </View>

        {/* INFO CARD SECTION */}
        <View style={{
          backgroundColor: theme.card,
          borderRadius: 16,
          marginBottom: 24,
          ...(Platform.OS === 'web'
            ? { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' } as any
            : { shadowColor: '#000', shadowOpacity: 0.08, shadowRadius: 8, elevation: 3 }),
          padding: 24,
          maxWidth: 600,
          alignSelf: 'center',
          width: '100%',
          ...glowStyle,
        }}>
          {/* General Info Section */}
          <Text style={[styles.sectionHeader, { color: theme.text }]}>General Info</Text>

          <View style={{ gap: isSmallScreen ? 12 : 8 }}>
                 <View style={rowStyle}>
                    <Text style={labelStyle}>Name</Text>
                    <Text style={valueStyle}>{item.name.replace(/\w\S*/g, (txt: string) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())}</Text>
                 </View>
                 <View style={rowStyle}>
                    <Text style={labelStyle}>Room</Text>
                    <Text style={valueStyle}>{item.rooms?.name ? item.rooms.name.replace(/\w\S*/g, (txt: string) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()) : '-'}</Text>
                 </View>
                 <View style={rowStyle}>
                    <Text style={labelStyle}>Category</Text>
                    <Text style={valueStyle}>{item.subcategory?.category?.name ? item.subcategory.category.name.replace(/\w\S*/g, (txt: string) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()) : '-'}</Text>
                 </View>
                 <View style={rowStyle}>
                    <Text style={labelStyle}>Subcategory</Text>
                    <Text style={valueStyle}>{item.subcategory?.name ? item.subcategory.name.replace(/\w\S*/g, (txt: string) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()) : '-'}</Text>
                 </View>
                 {/* Dynamic attributes moved up below subcategory (excluding core fields) */}
                 {attributeDefs.length > 0 && (item as any)?.attributes ? (
                   attributeDefs
                     .filter((def) => !['make', 'model', 'serial', 'serial_number'].includes(def.key))
                     .map((def) => {
                       const val = (item as any).attributes?.[def.key];
                       if (val === null || val === undefined || val === '') return null;
                       const display = typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val);
                       return (
                         <View key={def.id} style={rowStyle}>
                           <Text style={labelStyle}>{def.label}</Text>
                           <Text style={valueStyle}>{display}</Text>
                         </View>
                       );
                     })
                 ) : null}
                 {/* Make/Model/Serial now shown via dynamic attributes when defined */}
                 {attributeDefs.some(d => d.key === 'make') && (
                   <View style={rowStyle}>
                     <Text style={labelStyle}>Make</Text>
                     <Text style={valueStyle}>{(item as any).attributes?.make ? String((item as any).attributes.make) : '-'}</Text>
                   </View>
                 )}
                 {attributeDefs.some(d => d.key === 'model') && (
                   <View style={rowStyle}>
                     <Text style={labelStyle}>Model</Text>
                     <Text style={valueStyle}>{(item as any).attributes?.model ? String((item as any).attributes.model) : '-'}</Text>
                   </View>
                 )}
                 {attributeDefs.some(d => d.key === 'serial') && (
                   <View style={rowStyle}>
                     <Text style={labelStyle}>Serial Number</Text>
                     <Text style={valueStyle}>{(item as any).attributes?.serial ? String((item as any).attributes.serial) : '-'}</Text>
                   </View>
                 )}
                 
           </View>
          <View style={{ height: 1, backgroundColor: theme.border, marginVertical: 16 }} />
          {/* Purchase Info Section */}
          <Text style={[styles.sectionHeader, { color: theme.text }]}>Purchase Info</Text>
          <View style={{ gap: isSmallScreen ? 12 : 8 }}>
                 <View style={rowStyle}>
                    <Text style={labelStyle}>Price</Text>
                    <Text style={valueStyle}>{item.price ? formatPrice(item.price) : '-'}</Text>
                 </View>
                 <View style={rowStyle}>
                    <Text style={labelStyle}>Purchase Date</Text>
                    <Text style={valueStyle}>{item.purchase_date ? formatDate(item.purchase_date) : '-'}</Text>
                 </View>
                 <View style={rowStyle}>
                    <Text style={labelStyle}>Place of Purchase</Text>
                    <Text style={valueStyle}>{item.place_of_purchase ? item.place_of_purchase.replace(/\w\S*/g, (txt: string) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()) : '-'}</Text>
                 </View>
           </View>
          <View style={{ height: 1, backgroundColor: theme.border, marginVertical: 16 }} />
          
          {/* Warranty Info Section */}
          <Text style={[styles.sectionHeader, { color: theme.text }]}>Warranty Info</Text>
          <View style={{ gap: isSmallScreen ? 12 : 8 }}>
                 <View style={rowStyle}>
                    <Text style={labelStyle}>Warranty Expiry</Text>
                    <Text style={valueStyle}>{item.warranty_expiry ? formatDate(item.warranty_expiry) : '-'}</Text>
                 </View>
           </View>
          <View style={{ height: 1, backgroundColor: theme.border, marginVertical: 16 }} />
          {/* Description Section */}
          <Text style={[styles.sectionHeader, { color: theme.text }]}>Description</Text>
          <Text style={{ color: theme.text, fontSize: 16, lineHeight: 22, marginBottom: 8 }}>{item.description ? item.description.charAt(0).toUpperCase() + item.description.slice(1) : '-'}</Text>
          <View style={{ height: 1, backgroundColor: theme.border, marginVertical: 16 }} />
          {/* Documents Section */}
          <Text style={[styles.sectionHeader, { color: theme.text }]}>Documents</Text>
          {documents.length > 0 ? (
            <View style={{ gap: 8, marginTop: 8, marginBottom: 8 }}>
              {(() => {
                const rank = (t: string) => (t === 'receipt' ? 0 : t === 'warranty' ? 1 : 2);
                const ordered = [...documents].sort((a, b) => {
                  const r = rank(a.type) - rank(b.type);
                  if (r !== 0) return r;
                  const an = (a.name || '').toLowerCase();
                  const bn = (b.name || '').toLowerCase();
                  if (an && bn) return an.localeCompare(bn);
                  return 0;
                });
                return ordered.map(doc => {
                  const label =
                    doc.type === 'receipt'
                      ? 'Receipt'
                      : doc.type === 'warranty'
                      ? 'Warranty'
                      : (doc.name && doc.name.trim().length > 0 ? doc.name : 'Document');
                  return (
                    <View
                      key={doc.id}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: theme.inputBackground,
                        borderRadius: 10,
                        paddingVertical: 10,
                        paddingHorizontal: 12
                      }}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 1 }}>
                        <Ionicons
                          name={doc.type === 'receipt' ? 'receipt-outline' : doc.type === 'warranty' ? 'shield-checkmark-outline' : 'document-outline'}
                          size={18}
                          color={theme.accent}
                        />
                        <Text style={{ color: theme.text, fontSize: 15 }} numberOfLines={1}>
                          {label}
                        </Text>
                      </View>
                      <ThemedButton
                        title="View"
                        onPress={() => Linking.openURL(doc.signedUrl)}
                        style={{ minWidth: 96 }}
                        accessibilityLabel={`View ${label}`}
                      />
                    </View>
                  );
                });
              })()}
            </View>
          ) : (
            <View style={{ alignItems: 'center', marginBottom: 8 }}>
              <Text style={{ color: theme.secondaryText, fontSize: 14 }}>No documents uploaded</Text>
            </View>
          )}
        </View>
      </ScrollView>

      {/* Image Viewer Modal */}
      {Platform.OS === 'web' ? (
        <RNModal
          visible={imageViewerVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setImageViewerVisible(false)}
        >
          <View style={[styles.modalContent, { backgroundColor: 'rgba(0,0,0,0.9)' }]}>
            <Pressable
              onPress={() => setImageViewerVisible(false)}
              style={styles.closeButton}
            >
              <Ionicons name="close" size={24} color={closeIconColor} />
            </Pressable>
            {item.item_images && item.item_images.length > 0 ? (
              <View style={{ flex: 1, width: '100%', alignItems: 'center' }}>
                <Image
                  source={{ uri: signedImageUrls[selectedImageIndex] || '' }}
                  style={styles.modalImage}
                  resizeMode="contain"
                />
                {item.item_images.length > 1 && (
                  <View style={styles.thumbnailsContainer}>
                    {item.item_images.map((image, index) => (
                      <Pressable
                        key={image.id}
                        onPress={() => setSelectedImageIndex(index)}
                        style={[
                          styles.thumbnailWrapper,
                          selectedImageIndex === index && styles.selectedThumbnail
                        ]}
                      >
                        <Image
                          source={{ uri: signedImageUrls[index] || '' }}
                          style={styles.thumbnail}
                          resizeMode="cover"
                        />
                      </Pressable>
                    ))}
                  </View>
                )}
              </View>
            ) : (
              <View style={[styles.modalContent, styles.placeholderContainer]}>
                <Ionicons name="image-outline" size={64} color="#BBB" />
                <Text style={[styles.placeholderText, { color: themeType === 'light' ? '#BBB' : theme.text }]}>No Image</Text>
              </View>
            )}
            <View style={styles.bottomControls}>
              <View style={styles.navigationButtons}>
                <Pressable
                  style={({ pressed }) => [
                    styles.navButton,
                    selectedImageIndex === 0 && styles.navButtonDisabled,
                    pressed && styles.buttonPressed
                  ]}
                  onPress={() => {
                    if (selectedImageIndex > 0) {
                      setSelectedImageIndex(selectedImageIndex - 1);
                    }
                  }}
                  disabled={selectedImageIndex === 0}
                  accessibilityRole="button"
                  accessibilityLabel="Previous image"
                  accessibilityHint="Shows the previous image in the gallery"
                >
                  <Ionicons name="chevron-back" size={32} color="#fff" />
                </Pressable>
                <Pressable
                  onPress={() => setImageViewerVisible(false)}
                  style={styles.centerCloseButton}
                  accessibilityRole="button"
                  accessibilityLabel="Close image viewer"
                >
                  <View style={[styles.closeButtonContainer, { backgroundColor: theme.inputBackground }]}>
                    <Text style={[styles.closeButtonText, { color: theme.text }]}>Close</Text>
                  </View>
                </Pressable>
                <Pressable
                  style={({ pressed }) => [
                    styles.navButton,
                    item?.item_images && selectedImageIndex === item.item_images.length - 1 && styles.navButtonDisabled,
                    pressed && styles.buttonPressed
                  ]}
                  onPress={() => {
                    if (selectedImageIndex < item.item_images.length - 1) {
                      setSelectedImageIndex(selectedImageIndex + 1);
                    }
                  }}
                  disabled={item?.item_images && selectedImageIndex === item.item_images.length - 1}
                  accessibilityRole="button"
                  accessibilityLabel="Next image"
                  accessibilityHint="Shows the next image in the gallery"
                >
                  <Ionicons name="chevron-forward" size={32} color="#fff" />
                </Pressable>
              </View>
              <Text 
                style={[styles.imageCounter, { color: theme.text }]}
                accessibilityRole="text"
                accessibilityLabel={`Image ${selectedImageIndex + 1} of ${item?.item_images?.length}`}
              >
                {item.item_images && item.item_images.length > 0 ? `${selectedImageIndex + 1} / ${item.item_images.length}` : '0 / 0'}
              </Text>
            </View>
          </View>
        </RNModal>
      ) : (
        <Modal
          isVisible={imageViewerVisible}
          style={styles.modal}
          animationIn="fadeIn"
          animationOut="fadeOut"
          backdropTransitionOutTiming={0}
          useNativeDriver={true}
          accessibilityViewIsModal
          statusBarTranslucent
          onBackdropPress={() => setImageViewerVisible(false)}
          onBackButtonPress={Platform.OS === 'android' ? () => setImageViewerVisible(false) : undefined}
        >
          <View style={styles.modalContent}>
            <Pressable
              onPress={() => setImageViewerVisible(false)}
              style={styles.closeButton}
            >
              <Ionicons name="close" size={24} color={closeIconColor} />
            </Pressable>
            {item.item_images && item.item_images.length > 0 ? (
              <View style={{ flex: 1, width: '100%', alignItems: 'center' }}>
                <Image
                  source={{ uri: signedImageUrls[selectedImageIndex] || '' }}
                  style={styles.modalImage}
                  resizeMode="contain"
                />
                {item.item_images.length > 1 && (
                  <View style={styles.thumbnailsContainer}>
                    {item.item_images.map((image, index) => (
                      <Pressable
                        key={image.id}
                        onPress={() => setSelectedImageIndex(index)}
                        style={[
                          styles.thumbnailWrapper,
                          selectedImageIndex === index && styles.selectedThumbnail
                        ]}
                      >
                        <Image
                          source={{ uri: signedImageUrls[index] || '' }}
                          style={styles.thumbnail}
                          resizeMode="cover"
                        />
                      </Pressable>
                    ))}
                  </View>
                )}
              </View>
            ) : (
              <View style={[styles.modalContent, styles.placeholderContainer]}>
                <Ionicons name="image-outline" size={64} color="#BBB" />
                <Text style={[styles.placeholderText, { color: themeType === 'light' ? '#BBB' : theme.text }]}>No Image</Text>
              </View>
            )}
            <View style={styles.bottomControls}>
              <View style={styles.navigationButtons}>
                <Pressable
                  style={({ pressed }) => [
                    styles.navButton,
                    selectedImageIndex === 0 && styles.navButtonDisabled,
                    pressed && styles.buttonPressed
                  ]}
                  onPress={() => {
                    if (selectedImageIndex > 0) {
                      setSelectedImageIndex(selectedImageIndex - 1);
                    }
                  }}
                  disabled={selectedImageIndex === 0}
                  accessibilityRole="button"
                  accessibilityLabel="Previous image"
                  accessibilityHint="Shows the previous image in the gallery"
                >
                  <Ionicons name="chevron-back" size={32} color="#fff" />
                </Pressable>
                <Pressable
                  onPress={() => setImageViewerVisible(false)}
                  style={styles.centerCloseButton}
                  accessibilityRole="button"
                  accessibilityLabel="Close image viewer"
                >
                  <View style={[styles.closeButtonContainer, { backgroundColor: theme.inputBackground }]}>
                    <Text style={[styles.closeButtonText, { color: theme.text }]}>Close</Text>
                  </View>
                </Pressable>
                <Pressable
                  style={({ pressed }) => [
                    styles.navButton,
                    item?.item_images && selectedImageIndex === item.item_images.length - 1 && styles.navButtonDisabled,
                    pressed && styles.buttonPressed
                  ]}
                  onPress={() => {
                    if (selectedImageIndex < item.item_images.length - 1) {
                      setSelectedImageIndex(selectedImageIndex + 1);
                    }
                  }}
                  disabled={item?.item_images && selectedImageIndex === item.item_images.length - 1}
                  accessibilityRole="button"
                  accessibilityLabel="Next image"
                  accessibilityHint="Shows the next image in the gallery"
                >
                  <Ionicons name="chevron-forward" size={32} color="#fff" />
                </Pressable>
              </View>
              <Text 
                style={[styles.imageCounter, { color: theme.text }]}
                accessibilityRole="text"
                accessibilityLabel={`Image ${selectedImageIndex + 1} of ${item?.item_images?.length}`}
              >
                {item.item_images && item.item_images.length > 0 ? `${selectedImageIndex + 1} / ${item.item_images.length}` : '0 / 0'}
              </Text>
            </View>
          </View>
        </Modal>
      )}
    </SafeAreaView>
  );
};

// Add styles object at end before export default
const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...(Platform.OS === 'web' ? {} : Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
      },
      android: {
        elevation: 5,
      },
    })),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    color: '#ff3b30',
    fontSize: 16,
  },
  scrollContent: {
    flexGrow: 1,
  },
  mainImage: {
    width: '100%',
    height: '100%',
  },
  thumbnailsOuterContainer: {
    width: '100%',
    alignItems: 'center',
    paddingVertical: 12,
  },
  thumbnailsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 16,
    width: '100%',
    maxWidth: 800,
    position: 'absolute',
    bottom: 120,
  },
  thumbnailWrapper: {
    width: 60,
    height: 60,
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.3)',
  },
  selectedThumbnail: {
    borderColor: '#007AFF',
  },
  thumbnail: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  modal: {
    margin: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.9)',
  },
  modalImage: {
    width: '100%',
    height: '75%',
    resizeMode: 'contain',
  },
  closeButton: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 50 : 20,
    right: 20,
    zIndex: 1,
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  bottomControls: {
    position: 'absolute',
    bottom: 40,
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  navigationButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 20,
    marginBottom: 16,
  },
  navButton: {
    width: 48,
    height: 48,
    backgroundColor: 'rgba(0,0,0,0.65)',
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 8px rgba(0,0,0,0.18)' } as any
      : { shadowColor: '#000', shadowOpacity: 0.18, shadowRadius: 8, shadowOffset: { width: 0, height: 2 }, elevation: 6 }),
  },
  navButtonDisabled: { opacity: 0.5 },
  buttonPressed: { opacity: 0.7 },
  centerCloseButton: { marginHorizontal: 20 },
  closeButtonContainer: {
    paddingVertical: 8,
    paddingHorizontal: 24,
    borderRadius: 20,
    ...(Platform.OS === 'web' ? { boxShadow: '0 1px 4px rgba(0,0,0,0.15)' } as any : { elevation: 2 }),
  },
  closeButtonText: { fontSize: 16, fontWeight: '600', fontFamily: 'Inter_600SemiBold' },
  imageCounter: { color: '#fff', fontSize: 14, fontWeight: '600' },
  placeholderContainer: { justifyContent: 'center', alignItems: 'center', backgroundColor: 'transparent' },
  placeholderText: { marginTop: 16, fontSize: 16 },
  sectionHeader: {
    fontSize: 17,
    fontWeight: '500',
    marginBottom: 12,
  },
});

export default ViewItemScreen; 